import axios from "axios";

const api = axios.create({
   // Base URL comes from REACT_APP_API_BASE_URL (baked at build time), defaulting to the local
   // backend for dev. For a same-origin deploy (frontend served from the API's wwwroot), set it to
   // "" so requests go to the current origin. Was hardcoded to CHC's EST domain.
   baseURL: process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:5106/'
});

// SECURITY: attach the JWT (issued at login, stored in sessionStorage) to every request.
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token');
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

// SECURITY: on any 401 (e.g. the JWT expired), clear the session and bounce to login instead of
// leaving an authenticated-looking UI whose calls silently fail. Guarded so we don't loop on /login.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('isAuthenticated');
      sessionStorage.removeItem('username');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export interface TagMappingDto {
  id?: string;
  AddCrmTagId?: string | null;
  RemoveCrmTagId?: string | null;
  AddCerboTagId?: string | null;
  RemoveCerboTagId?: string | null;
  IsAdd?: boolean;
  IsCerbo?: boolean;
  HasValue?: boolean;
  CustomFieldId?: string | null;
  CustomFieldValue?: string | null;
}


export interface AppointmentMappingDto {
  id?: string | null;
  appointmentType?: string | null;
  hasTagCondition?: boolean;
  isStartOver?: boolean;
  requiredCerboTags?: string | null;
  requiredCRMTags?: string | null;
  customFieldJson?: string | null;
  providerId?: string | null;
  EnableProvider? : boolean;
  // Calendar feature: map this appointment type to a real GHL calendar (+ optional assigned user).
  ghlCalendarId?: string | null;
  ghlAssignedUserId?: string | null;
} 


export interface TaskMappingDto {
  id?: string;
  campaignTagId: string;
  cerboTaskSubject: string;
  taskDetails: string;
  cerboDoctorId: number;
  cerboPriority: string;
  createddateTime?: string;
  updatedDateTime?: string;
  isAddCerbo?: boolean;
  customFieldId?: string | null;
  customFieldValue?: string;
  hasValue: boolean;
  dueDate: string;
}

export interface QuestionnairesMappingDto {
  id?: string;
  questionnaryText?: string | null;
  enableTagCondition?: boolean;
  conditionTags?: string | null;
  cerboAddTags?: string | null;
  cerboRemoveTags?: string | null;
  crmAddTags?: string | null;
  crmRemoveTags?: string | null;
  customFieldId?: string | null;
  customFieldValue?: string | null;
  cerboNotesTagId?: string | null;
  cerboTagNotes?: string | null;
  createdDate?: string | null;
  updatedDate?: string | null;
}

export interface OpenOrderDto {
  id?: string;
  CerboTags: string;
  CRMTag: string;
  Ordername: string;
  CustomFieldId?: string | null;
  CustomFieldValue?: string | null;
  SelectedTagId?: string | null;
  TagNotes?: string | null;
}

function handleError(err: any): { success: false, error: string } {
  let errorMsg = 'Unknown error';
  if (err.response && typeof err.response.data === 'string') {
    errorMsg = err.response.data;
  } else if (err.message) {
    errorMsg = err.message;
  }
  return { success: false, error: errorMsg };
}
export async function Login(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const resp = await api.get("OAuth/Login");
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function CallBack(queryString: string): Promise<{ success: boolean, data?: any, error?: string }> {
  const url = `/api/Login/Callback${queryString}`;
  const res = await api.get(url, {
    headers: { 'Accept': 'application/json' }
  });
  if (res.status >= 200 && res.status < 300) {
    return { success: true, data: res.data };
  } else {
    return handleError(res);
  }
}


export async function OAuthConnected(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const resp = await api.get("api/Login/OAuthConnected");
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

// Manually trigger the full data sync (same job the 15-min poll runs). Can take 20-60s.
export async function RunSync(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const resp = await api.get("api/Sync/Sync");
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

// Manually trigger the appointment sync (Cerbo appointments -> GHL + calendar events).
export async function RunAppointmentSync(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const resp = await api.get("api/Sync/SyncAppointMent");
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

// Live status of the background sync jobs (running / current step / last completed).
export async function GetSyncStatus(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const resp = await api.get("api/Sync/Status");
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function CerboConnected(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const resp = await api.get("/Admin/CerboConnected");
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function OAuthDisConnect(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const resp = await api.get("api/Login/DisconnectOAuth");
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function GetLoginCredentials(userName: string, password: string): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    // POST to the token-issuing login (password no longer in the URL). data = { token, userName }
    const resp = await api.post('api/Login/Login', { userName, password });
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}


export async function getAllTagMappings(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const resp = await api.get("/Admin/GetAllTagMappings");
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function FetchMasters(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const res = await api.get("/Admin/SyncNewEntities");
    return { success: true, data: res.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function GetLatSyncedDateTime(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const res = await api.get("/Admin/GetLastSyncedDateTime");
    return { success: true, data: res.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function GetAllCRMTags(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const res = await api.get("/Admin/GetAllCRMTags");
    return { success: true, data: res.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function GetAllCerboTags(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const res = await api.get("/Admin/GetAllCerboTags");
    return { success: true, data: res.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function GetAllCustomFields(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const res = await api.get("/Admin/GetAllCustomFields");
    return { success: true, data: res.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function getAllAppointmentMappings(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const resp = await api.get("/Admin/GetAppointmentMappings");
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function GetTaskMappings(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const resp = await api.get("/Admin/GetTaskMappings");
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

// HighLevel -> Cerbo calendar-sync mappings (reverse direction)
export async function getCalendarCerboMappings(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const resp = await api.get("/Admin/GetCalendarCerboMappings");
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function saveOrEditCalendarCerboMapping(mapping: any): Promise<{ success: boolean, data?: string, error?: string }> {
  try {
    const resp = await api.post("/Admin/SaveOrEditCalendarCerboMapping", mapping);
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function deleteCalendarCerboMapping(id: string): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const resp = await api.delete(`/Admin/DeleteCalendarCerboMapping?id=${encodeURIComponent(id)}`, {
      headers: { 'Accept': 'application/json' }
    });
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function GetProviderList(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const resp = await api.get("/Admin/GetProviderList");
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

// Calendar feature: the connected location's real GHL calendars, for the mapping dropdown.
export async function getGhlCalendars(): Promise<{ success: boolean, data?: { id: string; name: string; teamMembers?: { userId: string }[] }[], error?: string }> {
  try {
    const resp = await api.get("/Admin/GetGhlCalendars");
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

// Questionnaire names the sync has already seen on charts (dropdown in the questionnaire mapping dialog).
export async function getQuestionnaireNames(): Promise<{ success: boolean, data?: string[], error?: string }> {
  try {
    const resp = await api.get("/Admin/GetQuestionnaireNames");
    return { success: true, data: Array.isArray(resp.data) ? resp.data : [] };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function getGhlUsers(): Promise<{ success: boolean, data?: { id: string; name: string; firstName?: string; lastName?: string }[], error?: string }> {
  try {
    const resp = await api.get("/Admin/GetGhlUsers");
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function GetAllContacts(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const resp = await api.get("/Admin/GetAllContacts");
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function GetQuestionnariesMappings(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const resp = await api.get("/Admin/GetQuestionnariesMappings");
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function GetAppointmentTypeEntities(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const resp = await api.get("/Admin/GetAppointmentTypeEntities");
    return { success: true, data: resp.data };
  }
  catch (err: any) {
    return handleError(err);
  }
}

export async function GetOrderEntities(): Promise<{ success: boolean, data?: any, error?: string }> {
  try {
    const resp = await api.get("/Admin/GetOrderEntities");
    return { success: true, data: resp.data };
  }
  catch (err: any) {
    return handleError(err);
  }
}

export async function deleteTagMapping(mappingId: string): Promise<{ success: boolean, data?: any, error?: string }> {
  const url = `/Admin/DeleteTagMapping?MappingId=${encodeURIComponent(mappingId)}`;
  const res = await api.delete(url, {
    headers: { 'Accept': 'application/json' }
  });
  if (res.status >= 200 && res.status < 300) {
    return { success: true, data: res.data };
  } else {
    return handleError(res);
  }
}

export async function deleteAppointmentMapping(mappingId: string): Promise<{ success: boolean, data?: any, error?: string }> {
  const url = `/Admin/DeleteAppointmentMapping?AppointmentMappingId=${encodeURIComponent(mappingId)}`;
  const res = await api.delete(url, {
    headers: { 'Accept': 'application/json' }
  });
  if (res.status >= 200 && res.status < 300) {
    return { success: true, data: res.data };
  } else {
    return handleError(res);
  }
}

export async function DeleteQuestionnary(mappingId: string): Promise<{ success: boolean, data?: any, error?: string }> {
  const url = `/Admin/DeleteQuestionnary?id=${encodeURIComponent(mappingId)}`;
  const res = await api.delete(url, {
    headers: { 'Accept': 'application/json' }
  });
  if (res.status >= 200 && res.status < 300) {
    return { success: true, data: res.data };
  } else {
    return handleError(res);
  }
}

export async function DeleteTaskMapping(mappingId: string): Promise<{ success: boolean, data?: any, error?: string }> {
  const url = `/Admin/DeleteTaskMapping?taskMappingId=${encodeURIComponent(mappingId)}`;
  const res = await api.delete(url, {
    headers: { 'Accept': 'application/json' }
  });
  if (res.status >= 200 && res.status < 300) {
    return { success: true, data: res.data };
  } else {
    return handleError(res);
  }
}

export async function DeleteOrderMapping(mappingId: string): Promise<{ success: boolean, data?: any, error?: string }> {
  const url = `/Admin/DeleteOrderMapping?orderMappingId=${encodeURIComponent(mappingId)}`;
  const res = await api.delete(url, {
    headers: { 'Accept': 'application/json' }
  });
  if (res.status >= 200 && res.status < 300) {
    return { success: true, data: res.data };
  } else {
    return handleError(res);
  }
}


export async function saveOrEditTaskMapping(dto: TaskMappingDto): Promise<{ success: boolean, data?: string, error?: string }> {
  try {
    const resp = await api.post('/Admin/SaveOrEditTaskMappings', dto, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/plain'
      },
      responseType: 'text'
    });
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function saveOrEditAppointmentMapping(dto: AppointmentMappingDto): Promise<{ success: boolean, data?: string, error?: string }> {
  try {
    const resp = await api.post('/Admin/SaveOrEditAppointmentMapping', dto, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/plain'
      },
      responseType: 'text'
    });
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function saveOrEditTagMapping(dto: TagMappingDto): Promise<{ success: boolean, data?: string, error?: string }> {
  try {
    const resp = await api.post('/Admin/SaveOrEditTagMapping', dto, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/plain'
      },
      responseType: 'text'
    });
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function SaveOrEditQuestionnariesMapping(dto: QuestionnairesMappingDto): Promise<{ success: boolean, data?: string, error?: string }> {
  try {
    const resp = await api.post('/Admin/SaveOrEditQuestionnariesMapping', dto, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/plain'
      },
      responseType: 'text'
    });
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}

export async function SaveOrEditOrders(dto: OpenOrderDto): Promise<{ success: boolean, data?: string, error?: string }> {
  try {
    const resp = await api.post('/Admin/SaveOrEditOrders', dto, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/plain'
      },
      responseType: 'text'
    });
    return { success: true, data: resp.data };
  } catch (err: any) {
    return handleError(err);
  }
}